import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL + 'v1/orders';

class orderService {
    /**
     * Tworzy nowe zamówienie, wysyłając dane zamówienia do endpointu.
     * 
     * @param {Object} order - Obiekt zawierający dane nowego zamówienia.
     * @returns {Promise<Object>} Zwraca odpowiedź z serwera po pomyślnym utworzeniu zamówienia.
     */
    createOrder(order) {
        return axios.post(API_URL, order);
    }

    /**
     * Pobiera listę wszystkich zamówień.
     * 
     * @returns {Promise<Object>} Zwraca odpowiedź z serwera zawierającą listę wszystkich zamówień.
     */
    getAllOrders() {
        return axios.get(API_URL);
    }

    /**
     * Pobiera zamówienia przypisane do określonej firmy i dyspozytora.
     * 
     * @param {number|string} companyId - ID firmy, której zamówienia chcemy pobrać.
     * @param {number|string} dispatcherId - ID dyspozytora, którego zamówienia chcemy pobrać.
     * @returns {Promise<Object>} Zwraca dane dotyczące zamówień przypisanych do określonej firmy i dyspozytora.
     */
    getOrdersByCompany(companyId, dispatcherId) {
        return axios.get(`${API_URL}/company/${companyId}/dispatcher/${dispatcherId}`);
    }

    /**
     * Pobiera zamówienia przypisane do określonego dyspozytora.
     * 
     * @param {number|string} dispatcherId - ID dyspozytora, którego zamówienia chcemy pobrać.
     * @returns {Promise<Object>} Zwraca dane dotyczące zamówień przypisanych do określonego dyspozytora.
     */
    getOrdersByDispatcher(dispatcherId) {
        return axios.get(`${API_URL}/dispatcher/${dispatcherId}`);
    }

    /**
     * Pobiera zamówienia przypisane do określonego dyspozytora.
     * 
     * @param {number|string} dispatcherId - ID dyspozytora, którego zamówienia chcemy pobrać.
     * @returns {Promise<Object>} Zwraca dane dotyczące zamówień przypisanych do określonego dyspozytora.
     */
    getArchiveOrdersByDispatcher(dispatcherId) {
        return axios.get(`${API_URL}/archive/dispatcher/${dispatcherId}`);
    }

    /**
     * Pobiera dane zamówienia na podstawie jego identyfikatora (ID).
     * 
     * @param {number|string} id - ID zamówienia, które chcemy pobrać.
     * @returns {Promise<Object>} Zwraca dane zamówienia o podanym ID.
     */
    getOrderById(id) {
        return axios.get(`${API_URL}/${id}`);
    }

    /**
     * Aktualizuje dane zamówienia na podstawie jego identyfikatora (ID).
     * 
     * @param {number|string} id - ID zamówienia, które chcemy zaktualizować.
     * @param {Object} updatedOrder - Obiekt zawierający zaktualizowane dane zamówienia.
     * @returns {Promise<Object>} Zwraca odpowiedź z serwera po pomyślnej aktualizacji zamówienia.
     */
    updateOrder(id, updatedOrder) {
        return axios.put(`${API_URL}/${id}`, updatedOrder);
    }

    /**
     * Usuwa zamówienie na podstawie jego identyfikatora (ID).
     * 
     * @param {number|string} id - ID zamówienia, które chcemy usunąć.
     * @returns {Promise<void>} Zwraca odpowiedź z serwera po pomyślnym usunięciu zamówienia.
     */
    deleteOrder(id) {
        return axios.delete(`${API_URL}/${id}`);
    }

    restoreOrder(id) {
        return axios.put(`${API_URL}/restore/${id}`);
    }

    /**
     * Pobiera aktualne zamówienia dla zalogowanego kierowcy.
     * 
     * @returns {Promise<Object>} Zwraca dane aktualnego zamówienia.
     */
    fetchOrders(driverId) {
        return axios.get(`${API_URL}/driver/${driverId}`);
    }

    /**
     * Pobiera aktualne zamówienie dla zalogowanego kierowcy.
     * 
     * @returns {Promise<Object>} Zwraca dane aktualnego zamówienia.
     */
    fetchCurrentOrders(driverId) {
        return axios.get(`${API_URL}/current/${driverId}`);
    }

    /**
     * Akceptuje zamówienie dla kierowcy.
     * 
     * @param {number|string} orderId - ID zamówienia do zaakceptowania.
     * @returns {Promise<Object>} Zwraca odpowiedź z serwera po pomyślnym zaakceptowaniu zamówienia.
     */
    acceptOrder(orderId, driverId) {
        return axios.post(`${API_URL}/${orderId}/accept/${driverId}`);
    }

    /**
     * Odrzuca zamówienie dla kierowcy.
     * 
     * @param {number|string} orderId - ID zamówienia do odrzucenia.
     * @returns {Promise<Object>} Zwraca odpowiedź z serwera po pomyślnym odrzuceniu zamówienia.
     */
    rejectOrder(orderId) {
        return axios.post(`${API_URL}/${orderId}/reject`);
    }

    completeOrder(orderId, driverId) {
        return axios.put(`${API_URL}/${orderId}/complete/${driverId}`);
    }

    startOrder(orderId, driverId) {
        return axios.put(`${API_URL}/${orderId}/startOrder/${driverId}`);
    }

    startPassenger(orderId, passengerId) {
        return axios.put(`${API_URL}/${orderId}/start/${passengerId}`);
    }

    endPassenger(orderId, passengerId) {
        return axios.put(`${API_URL}/${orderId}/end/${passengerId}`);
    }

    waitingDropOffPassenger(orderId, passengerId) {
        return axios.put(`${API_URL}/${orderId}/waitingDrop/${passengerId}`);
    }
    
    dropPassenger(orderId, passengerId) {
        return axios.put(`${API_URL}/${orderId}/drop/${passengerId}`);
    }
    
    updatePayoff(orderId, updatedOrder) {
        return axios.put(`${API_URL}/${orderId}/updateKilometersAndPrice`, updatedOrder);
    }

    updateKilometers(orderId, updatedOrder) {
        return axios.put(`${API_URL}/${orderId}/updateKilometers`, updatedOrder);
    }

    updateDriverGate(orderId, price) {
        return axios.put(`${API_URL}/${orderId}/driverGate`, price);
    }
}

export default new orderService();
